import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import SEO from "@/components/seo/SEO";
import GoogleReviews from "@/components/GoogleReviews";
import { useContenido } from "@/context/ContenidoContext";
import { esMartes } from "@/lib/horario";

/**
 * Reservas — Public booking form with:
 *  - Live availability check (/api/reservas/disponibilidad?fecha=…) so full
 *    slots are disabled and "X plazas libres" appears next to each hour.
 *  - Anti-overbooking guard server-side (409 if the slot fills between fetch
 *    and submit); the UI re-fetches availability after every failed submit.
 *  - Two-tap WhatsApp button that opens wa.me with a pre-filled message
 *    composed from the fields the user already typed — converts 20-30%
 *    better than the form on mobile when users are in a hurry.
 *
 * i18n: All visible strings go through `t()` so the page renders in ES/EN/FR
 * based on the active language. The booking validation messages (toast.error)
 * are also translated; the backend responds in Spanish — those are surfaced
 * as-is which is acceptable as a fallback (server errors are rare for users
 * who actually validate their input client-side).
 */
export default function Reservas() {
    const { t, i18n } = useTranslation();
    const { config } = useContenido();
    const [form, setForm] = useState({
        nombre: "",
        email: "",
        telefono: "",
        fecha: "",
        hora: "",
        personas: 2,
        comentarios: "",
    });
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);
    const [availability, setAvailability] = useState(null);

    const cerrado = esMartes(form.fecha);
    const today = new Date().toISOString().slice(0, 10);

    /** Refetch availability whenever the date changes. */
    useEffect(() => {
        if (!form.fecha) {
            setAvailability(null);
            return;
        }
        let cancelled = false;
        api.get("/reservas/disponibilidad", { params: { fecha: form.fecha } })
            .then((r) => !cancelled && setAvailability(r.data))
            .catch(() => !cancelled && setAvailability(null));
        return () => {
            cancelled = true;
        };
    }, [form.fecha]);

    /** All slots for the current date (whether full or not), in display order. */
    const slots = availability?.slots || [];

    /** When the user changes "personas", a slot is considered full if it cannot
     *  accommodate them anymore. */
    const isSlotUnavailable = (slot) =>
        slot.completo || slot.plazas < Number(form.personas || 1);

    /** Pre-filled WhatsApp deeplink — bypasses the form for users in a rush. */
    const whatsappLink = useMemo(() => {
        const number = (config?.whatsapp || "+34661428062").replace(/[^0-9]/g, "");
        const lines = [
            `Hola, me gustaría reservar mesa en el Restaurante Museo Tenería.`,
            form.nombre ? `Nombre: ${form.nombre}` : null,
            form.personas ? `Personas: ${form.personas}` : null,
            form.fecha ? `Fecha: ${form.fecha}` : null,
            form.hora ? `Hora: ${form.hora}` : null,
            form.comentarios ? `Comentarios: ${form.comentarios}` : null,
            form.telefono ? `Teléfono: ${form.telefono}` : null,
        ].filter(Boolean);
        const text = encodeURIComponent(lines.join("\n"));
        return `https://wa.me/${number}?text=${text}`;
    }, [form, config?.whatsapp]);

    const handle = (k) => (e) => {
        const v = e.target.value;
        setForm((f) => ({ ...f, [k]: v, ...(k === "fecha" ? { hora: "" } : {}) }));
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!form.nombre || !form.fecha || !form.hora) {
            toast.error(t("booking.errors.missing"));
            return;
        }
        if (cerrado) {
            toast.error(t("booking.errors.tuesday"));
            return;
        }
        setSending(true);
        try {
            await api.post("/reservas", {
                ...form,
                personas: Number(form.personas),
                email: form.email || null,
                telefono: form.telefono || null,
                comentarios: form.comentarios || null,
            });
            setDone(true);
            if (window.gtag) { window.gtag("event", "reserva_enviada", { event_category: "conversion", event_label: "Reserva online", value: 1 }); }
            toast.success(t("booking.success.toast"));
        } catch (err) {
            toast.error(formatApiError(err));
            // Re-fetch availability — the failure may be due to the slot filling
            // up between the page load and the submit.
            if (form.fecha) {
                api.get("/reservas/disponibilidad", { params: { fecha: form.fecha } })
                    .then((r) => setAvailability(r.data))
                    .catch(() => {});
            }
        } finally {
            setSending(false);
        }
    };

    return (
        <article className="pt-32 pb-24 paper-grain">
            <SEO
                title={t("booking.seo.title")}
                description={t("booking.seo.description")}
                keywords="reservar mesa Ocaña, reservas restaurante Toledo, reservar Tenería, mesa restaurante Ocaña"
                path="/reservas"
                breadcrumbs={[
                    { name: t("nav.home"), item: "/" },
                    { name: t("nav.reservas"), item: "/reservas" },
                ]}
            />
            <section className="max-w-3xl mx-auto px-6 text-center mb-20">
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                    className="roman-numeral"
                >
                    — {t("booking.hero.kicker")} —
                </motion.p>
                <motion.h1
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                    className="font-display font-light text-[clamp(2.5rem,7vw,6rem)] leading-[0.95] text-ivory mt-8"
                >
                    {t("booking.hero.title1")}{" "}
                    <span className="italic text-gold">{t("booking.hero.title2")}</span>
                </motion.h1>
                <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 0.8, duration: 1 }}
                    className="line-gold mx-auto my-10 origin-left"
                />
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5, duration: 1.2 }}
                    className="font-body text-stone"
                >
                    {t("booking.hero.subtitle")}
                </motion.p>
            </section>

            {done ? (
                <section className="max-w-2xl mx-auto px-6">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.6 }}
                        className="border border-gold/40 p-14 text-center"
                    >
                        <p className="font-display italic text-5xl text-gold">
                            {t("booking.success.title")}
                        </p>
                        <p className="font-body text-stone mt-8 leading-relaxed">
                            {t("booking.success.body")}{" "}
                            <a href="tel:+34925484633" className="text-gold" data-cursor="hover">
                                925 48 46 33
                            </a>
                            .
                        </p>
                        <button
                            type="button"
                            onClick={() => {
                                setDone(false);
                                setForm({
                                    nombre: "",
                                    email: "",
                                    telefono: "",
                                    fecha: "",
                                    hora: "",
                                    personas: 2,
                                    comentarios: "",
                                });
                            }}
                            className="btn-ghost mt-10"
                            data-testid="new-booking-button"
                            data-cursor="hover"
                        >
                            {t("booking.success.new")}
                        </button>
                    </motion.div>
                </section>
            ) : (
                <motion.section
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 1 }}
                    className="max-w-2xl mx-auto px-6"
                >
                    <form
                        onSubmit={submit}
                        data-testid="booking-form"
                        className="grid md:grid-cols-2 gap-x-10 gap-y-8"
                    >
                        <div className="md:col-span-2">
                            <label className="line-label">{t("booking.fields.name")} *</label>
                            <input
                                required
                                value={form.nombre}
                                onChange={handle("nombre")}
                                className="input-line"
                                data-testid="booking-name"
                            />
                        </div>
                        <div>
                            <label className="line-label">{t("booking.fields.email")}</label>
                            <input
                                type="email"
                                value={form.email}
                                onChange={handle("email")}
                                className="input-line"
                                data-testid="booking-email"
                            />
                        </div>
                        <div>
                            <label className="line-label">{t("booking.fields.phone")}</label>
                            <input
                                value={form.telefono}
                                onChange={handle("telefono")}
                                className="input-line"
                                data-testid="booking-phone"
                            />
                        </div>
                        <div>
                            <label className="line-label">{t("booking.fields.date")} *</label>
                            <input
                                type="date"
                                required
                                min={today}
                                value={form.fecha}
                                onChange={handle("fecha")}
                                className="input-line"
                                data-testid="booking-date"
                            />
                            {cerrado && (
                                <p className="text-oxblood text-xs font-editorial italic mt-2">
                                    {t("booking.tuesdayClosed")}
                                </p>
                            )}
                        </div>
                        <div>
                            <label className="line-label">{t("booking.fields.time")} *</label>
                            <select
                                required
                                value={form.hora}
                                onChange={handle("hora")}
                                disabled={!form.fecha || cerrado || !slots.length}
                                className="input-line bg-bg disabled:opacity-40"
                                data-testid="booking-time"
                            >
                                <option value="">
                                    {!form.fecha
                                        ? t("booking.fields.timePlaceholderNoDate")
                                        : cerrado
                                        ? t("booking.fields.timePlaceholderClosed")
                                        : t("booking.fields.timePlaceholder")}
                                </option>
                                {slots.map((slot) => {
                                    const unavailable = isSlotUnavailable(slot);
                                    return (
                                        <option
                                            key={slot.hora}
                                            value={slot.hora}
                                            disabled={unavailable}
                                        >
                                            {slot.hora}
                                            {unavailable
                                                ? ` · ${t("booking.full")}`
                                                : slot.plazas <= 5
                                                ? ` · ${slot.plazas} ${t("booking.seatsLeft")}`
                                                : ""}
                                        </option>
                                    );
                                })}
                            </select>
                            {availability?.slots?.length > 0 &&
                                slots.every(isSlotUnavailable) && (
                                    <p className="text-oxblood text-xs font-editorial italic mt-2">
                                        {t("booking.dayFull")}
                                    </p>
                                )}
                        </div>
                        <div>
                            <label className="line-label">
                                {t("booking.fields.people")} *
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="50"
                                required
                                value={form.personas}
                                onChange={handle("personas")}
                                className="input-line"
                                data-testid="booking-people"
                            />
                        </div>
                        <div className="md:col-span-2">
                            <label className="line-label">
                                {t("booking.fields.comments")}
                            </label>
                            <textarea
                                rows={3}
                                value={form.comentarios}
                                onChange={handle("comentarios")}
                                className="input-line resize-none"
                                data-testid="booking-comments"
                            />
                        </div>
                        <div className="md:col-span-2 flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
                            <button
                                type="submit"
                                disabled={sending || cerrado}
                                className="btn-solid-alt disabled:opacity-50"
                                data-testid="booking-submit-button"
                                data-cursor="hover"
                            >
                                {sending
                                    ? t("booking.cta.sending")
                                    : t("booking.cta.submit")}
                            </button>
                            {/* WhatsApp 2-tap: opens wa.me with the same data already filled */}
                            <a
                                href={whatsappLink}
                                target="_blank"
                                rel="noreferrer noopener"
                                data-testid="booking-whatsapp-button"
                                data-cursor="hover"
                                className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10 transition-colors font-body text-xs uppercase tracking-[0.2em]"
                            >
                                <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
                                </svg>
                                {t("booking.cta.whatsapp")}
                            </a>
                        </div>
                    </form>

                    {/* Real Google reviews — compact social-proof block */}
                    <div className="mt-16 md:mt-20 max-w-2xl mx-auto px-4 sm:px-0">
                        <GoogleReviews variant="compact" />
                    </div>
                </motion.section>
            )}
        </article>
    );
}
